import { useStreamingReport } from '../../hooks/useStreamingReport';
import StreamingStatus from '../../components/StreamingStatus';
import React, { useEffect, useMemo } from 'react';
import { Button, Form, Select, ConfigProvider, message, Checkbox, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import zhCN from 'antd/locale/zh_CN';
import dayjs from 'dayjs';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import 'dayjs/locale/zh-cn';
import { SolarDay, SolarTime } from 'tyme4ts';
import { Solar } from 'lunar-typescript';
import { useIztro } from 'iztro-hook';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocation, useNavigate } from 'react-router-dom';

import '../../App.css';
import BirthDateTimePicker from '../../components/BirthDateTimePicker';
import FourPillars from '../../components/FourPillars';
import AILoadingAnimation from '../../components/AILoadingAnimation';
import XunhupayButton from '../../components/XunhupayButton';
import InviteCodeUnlock from '../../components/InviteCodeUnlock';
import WeChatGuide from '../../components/WeChatGuide';
import { siteConfig } from '../../config/site';
import { CITY_OPTIONS, cityFilterOption, type City } from '../../data/cities';
import { calculateDaYunWithAge, type DaYunWithAge, ZIWEI_CONFIG, buildZiWeiData, isWeChatBrowser } from '../../utils';
import { useExportImage } from '../../hooks/useExportImage';
import QRCode from '../../assets/0328.jpg';

dayjs.locale('zh-cn');
dayjs.extend(dayOfYear);

const DEFAULT_ZIWEI_BIRTHDAY = '2000-1-1';
const CAREER_FOLLOW_UP_LIMIT = 2;
const CAREER_FORM_DRAFT_STORAGE_KEY = 'career:form-draft:v1';
const rawCareerPrice = Number(import.meta.env.VITE_CAREER_PRICE || '9.90');
const CAREER_PAYMENT_PRICE = Number.isFinite(rawCareerPrice) && rawCareerPrice > 0 ? rawCareerPrice : 9.9;

function decodeCareerReport(payload: string): { aiAnswer?: string } | null {
  try {
    return JSON.parse(decodeURIComponent(atob(payload)));
  } catch {
    return null;
  }
}

interface FormValues {
  birthDateTime: {
    date: dayjs.Dayjs;
    isLunar: boolean;
  };
  gender: 'male' | 'female';
  city?: string;
  longitude: number;
  latitude?: number;
  distinguishZiShi?: boolean;
  currentJob?: string;
  careerConfusion?: string;
  rationalVersion?: boolean;
}

interface CareerFormDraft {
  birthDateTime?: string;
  isLunar?: boolean;
  gender?: 'male' | 'female';
  city?: string;
  longitude?: number;
  latitude?: number;
  distinguishZiShi?: boolean;
  currentJob?: string;
  careerConfusion?: string;
  rationalVersion?: boolean;
}

function saveCareerFormDraft(values: Partial<FormValues>) {
  const birthDateTime = values.birthDateTime;
  const draft: CareerFormDraft = {
    birthDateTime: birthDateTime?.date?.toISOString(),
    isLunar: birthDateTime?.isLunar,
    gender: values.gender,
    city: values.city,
    longitude: values.longitude,
    latitude: values.latitude,
    distinguishZiShi: values.distinguishZiShi,
    currentJob: values.currentJob,
    careerConfusion: values.careerConfusion,
    rationalVersion: values.rationalVersion,
  };
  localStorage.setItem(CAREER_FORM_DRAFT_STORAGE_KEY, JSON.stringify(draft));
}

function loadCareerFormDraft(): CareerFormDraft | null {
  try {
    const raw = localStorage.getItem(CAREER_FORM_DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw) as CareerFormDraft;
    return draft && typeof draft === 'object' ? draft : null;
  } catch {
    localStorage.removeItem(CAREER_FORM_DRAFT_STORAGE_KEY);
    return null;
  }
}

interface CareerPrefillPayload {
  birthDateTime: {
    date: string;
    isLunar: boolean;
  };
  gender: 'male' | 'female';
  city?: string;
  longitude: number;
  latitude?: number;
  distinguishZiShi?: boolean;
  careerConfusion?: string;
  rationalVersion?: boolean;
}

function toTrueSolarTimeWithMinutes(datetime: dayjs.Dayjs, longitude: number, timeZoneOffset: number = 8): dayjs.Dayjs {
  const n = datetime.dayOfYear();
  const B = (2 * Math.PI * (n - 81)) / 364;
  const eot = 9.87 * Math.sin(2 * B) - 7.53 * Math.cos(B) - 1.5 * Math.sin(B);
  const zoneLongitude = 15 * timeZoneOffset;
  const deltaLon = 4 * (longitude - zoneLongitude);
  const deltaMinutes = eot + deltaLon;
  return datetime.add(deltaMinutes, 'minute');
}

function hourToShiChenIndex(hour: number): number {
  return Math.floor((hour + 1) / 2);
}

function getShiChenNameByHour(hour: number) {
  const normalizedHour = ((hour % 24) + 24) % 24;
  const shiChenNames = ['子时', '丑时', '寅时', '卯时', '辰时', '巳时', '午时', '未时', '申时', '酉时', '戌时', '亥时'];
  const index = normalizedHour === 23 ? 0 : Math.floor((normalizedHour + 1) / 2);
  return shiChenNames[index] || '未知时辰';
}

function getShiChenBoundaryHint(standardDatetime: dayjs.Dayjs, trueSolarDatetime: dayjs.Dayjs) {
  const totalMinutes = trueSolarDatetime.hour() * 60 + trueSolarDatetime.minute();
  const thresholdMinutes = 15;
  const boundaries = [
    { minuteOfDay: 60, prev: '子时', next: '丑时' },
    { minuteOfDay: 180, prev: '丑时', next: '寅时' },
    { minuteOfDay: 300, prev: '寅时', next: '卯时' },
    { minuteOfDay: 420, prev: '卯时', next: '辰时' },
    { minuteOfDay: 540, prev: '辰时', next: '巳时' },
    { minuteOfDay: 660, prev: '巳时', next: '午时' },
    { minuteOfDay: 780, prev: '午时', next: '未时' },
    { minuteOfDay: 900, prev: '未时', next: '申时' },
    { minuteOfDay: 1020, prev: '申时', next: '酉时' },
    { minuteOfDay: 1140, prev: '酉时', next: '戌时' },
    { minuteOfDay: 1260, prev: '戌时', next: '亥时' },
    { minuteOfDay: 1380, prev: '亥时', next: '子时' },
  ];

  for (const boundary of boundaries) {
    const diff = Math.abs(totalMinutes - boundary.minuteOfDay);
    if (diff <= thresholdMinutes) {
      return `当前真太阳时落在时辰交界前后 ${thresholdMinutes} 分钟内，接近${boundary.prev}/${boundary.next}交界，时柱可能受临界分钟影响。建议把两个时辰都试一下对比结果；如果你很在意定盘准确度，也可以联系我们专业客服咨询定盘服务。`;
    }
  }

  const standardShiChen = getShiChenNameByHour(standardDatetime.hour());
  const trueSolarShiChen = getShiChenNameByHour(trueSolarDatetime.hour());
  if (standardShiChen !== trueSolarShiChen) {
    return `按真太阳时换算后，已经和北京时间跨时辰了：北京时间是${standardShiChen}，真太阳时是${trueSolarShiChen}。这类情况建议你自己确认一下，也可以把两个时辰都试试；如果拿不准，也可以联系我们做定盘。`;
  }

  return '';
}

export const CareerPage = () => {
  const [form] = Form.useForm();
  const reportRef = React.useRef<HTMLDivElement | null>(null);
  const { exportImage } = useExportImage('个人发展分析报告');
  const location = useLocation();
  const navigate = useNavigate();

  const [fourPillars, setFourPillars] = React.useState<{ year: string; month: string; day: string; hour: string } | null>(null);
  const [daYun, setDaYun] = React.useState<DaYunWithAge[]>([]);
  const [lunarDateStr, setLunarDateStr] = React.useState<string>('');
  const [trueSolarTimeStr, setTrueSolarTimeStr] = React.useState<string>('');
  const [shiChenBoundaryHint, setShiChenBoundaryHint] = React.useState<string>('');

  const [aiLoading, setAiLoading] = React.useState(false);
  const { run: runReportStream, streaming: reportStreaming, error: streamError, access: reportAccess, reportToken: streamReportToken } = useStreamingReport();
  const [followUpLoading, setFollowUpLoading] = React.useState(false);
  const [liveFollowUp, setLiveFollowUp] = React.useState('');
  const { run: runFollowUpStream, streaming: followUpStreaming, error: followUpStreamError } = useStreamingReport();
  const [isWeChat, setIsWeChat] = React.useState(false);
  const [careerAnswer, setCareerAnswer] = React.useState<string>('');
  const reportHasContent = Boolean(careerAnswer);
  const [careerPreviewLocked, setCareerPreviewLocked] = React.useState(false);
  const [careerUnlocked, setCareerUnlocked] = React.useState(false);
  const [careerReportToken, setCareerReportToken] = React.useState('');
  const [paidCareerOrder, setPaidCareerOrder] = React.useState('');
  const [lockedSectionTitles, setLockedSectionTitles] = React.useState<string[]>([]);
  const [vedicDebug, setVedicDebug] = React.useState<string>('');
  const [followUpQuestion, setFollowUpQuestion] = React.useState('');
  const [followUpHistory, setFollowUpHistory] = React.useState<Array<{ question: string; answer: string }>>([]);
  const remainingFollowUps = CAREER_FOLLOW_UP_LIMIT - followUpHistory.length;
  const isCareerPaywallActive = (careerPreviewLocked || reportAccess === 'preview') && !careerUnlocked && !(reportStreaming && reportAccess === 'full');

  const [ziWeiBirthday, setZiWeiBirthday] = React.useState<string>('');
  const [ziWeiBirthTime, setZiWeiBirthTime] = React.useState<number>(0);
  const [ziWeiGender, setZiWeiGender] = React.useState<'男' | '女'>('女');

  console.log('vedicDebug', vedicDebug)

  const { astrolabe } = useIztro({
    birthday: ziWeiBirthday || DEFAULT_ZIWEI_BIRTHDAY,
    birthTime: ziWeiBirthTime,
    gender: ziWeiGender,
    birthdayType: 'solar',
    fixLeap: true,
    lang: 'zh-CN',
    ...(ZIWEI_CONFIG as any),
  });

  const ziWeiData = useMemo(() => {
    if (!astrolabe || !ziWeiBirthday || ziWeiBirthday === DEFAULT_ZIWEI_BIRTHDAY) {
      return null;
    }
    const currentYear = new Date().getFullYear();
    return buildZiWeiData(astrolabe, ziWeiBirthTime, [
      currentYear,
      currentYear + 1,
      currentYear + 2,
      currentYear + 3,
      currentYear + 4,
    ]);
  }, [astrolabe, ziWeiBirthday, ziWeiBirthTime]);

  const cityOptions = React.useMemo(() => CITY_OPTIONS, []);
  const homePath = siteConfig.isPaidMode ? '/lite' : '/';
  const donateQrUrl = (import.meta.env.VITE_DONATE_QR_URL as string | undefined)?.trim() || QRCode;

  useEffect(() => {
    setIsWeChat(isWeChatBrowser());
  }, []);

  const handleCityChange = (_cityName: string, option: any) => {
    const selectedCity = option.city as City;
    form.setFieldsValue({
      longitude: selectedCity.longitude,
      latitude: selectedCity.latitude,
    });
  };

  const performPaiPan = (birthInfo: {
    year?: number;
    month?: number;
    day?: number;
    hour?: number;
    minute?: number;
    solar?: string;
    longitude: number;
    gender: 'male' | 'female';
    distinguishZiShi?: boolean;
  }) => {
    let inputYear: number;
    let inputMonth: number;
    let inputDay: number;
    let inputHour: number;
    let inputMinute: number;

    if (birthInfo.solar) {
      const solarDate = dayjs(birthInfo.solar);
      inputYear = solarDate.year();
      inputMonth = solarDate.month() + 1;
      inputDay = solarDate.date();
      inputHour = solarDate.hour();
      inputMinute = solarDate.minute();
    } else {
      inputYear = birthInfo.year!;
      inputMonth = birthInfo.month!;
      inputDay = birthInfo.day!;
      inputHour = birthInfo.hour!;
      inputMinute = birthInfo.minute!;
    }

    const { longitude, gender, distinguishZiShi = false } = birthInfo;
    const standard = dayjs(
      `${inputYear}-${inputMonth.toString().padStart(2, '0')}-${inputDay
        .toString()
        .padStart(2, '0')} ${inputHour.toString().padStart(2, '0')}:${inputMinute
          .toString()
          .padStart(2, '0')}:00`,
    );
    const trueSolar = toTrueSolarTimeWithMinutes(standard, longitude, 8);

    let standardYear = standard.year();
    let standardMonth = standard.month() + 1;
    let standardDay = standard.date();
    const standardHour = standard.hour();
    const standardMinute = standard.minute();

    const isInZiShi = standardHour === 23 || standardHour === 0;
    if (isInZiShi && !distinguishZiShi && standardHour === 23) {
      const nextDay = standard.add(1, 'day');
      standardYear = nextDay.year();
      standardMonth = nextDay.month() + 1;
      standardDay = nextDay.date();
    }

    const year = trueSolar.year();
    const month = trueSolar.month() + 1;
    const day = trueSolar.date();
    const trueSolarHour = trueSolar.hour();
    const trueSolarMinute = trueSolar.minute();

    setTrueSolarTimeStr(trueSolar.format('YYYY年MM月DD日 HH:mm'));
    setShiChenBoundaryHint(getShiChenBoundaryHint(standard, trueSolar));

    const solarDayStandard = SolarDay.fromYmd(standardYear, standardMonth, standardDay);
    const lunarDayStandard = solarDayStandard.getLunarDay();
    const solarObj = Solar.fromDate(standard.toDate());
    const lunarObj = solarObj.getLunar();

    const solarHourTrue = SolarTime.fromYmdHms(year, month, day, trueSolarHour, trueSolarMinute, 0);
    const lunarHourTrue = solarHourTrue.getLunarHour();

    const sixtyCycleYear = lunarObj.getYearInGanZhiExact();
    let sixtyCycleMonth = lunarDayStandard.getMonthSixtyCycle().getName();
    const sixtyCycleDay = lunarDayStandard.getSixtyCycle().getName();
    const sixtyCycleHour = lunarHourTrue.getSixtyCycle().getName();

    try {
      const currentJieQi = lunarObj.getCurrentJieQi();

      if (currentJieQi && currentJieQi.isJie()) {
        const jieQiSolar = currentJieQi.getSolar();
        const birthTimestamp = standard.valueOf();
        const jieQiTimestamp = new Date(
          jieQiSolar.getYear(),
          jieQiSolar.getMonth() - 1,
          jieQiSolar.getDay(),
          jieQiSolar.getHour(),
          jieQiSolar.getMinute(),
          jieQiSolar.getSecond(),
        ).getTime();

        if (birthTimestamp < jieQiTimestamp) {
          const prevJieQi = lunarObj.getPrevJieQi();
          if (prevJieQi) {
            const prevJieQiSolar = prevJieQi.getSolar();
            const prevSolar = Solar.fromYmd(
              prevJieQiSolar.getYear(),
              prevJieQiSolar.getMonth(),
              prevJieQiSolar.getDay(),
            );
            sixtyCycleMonth = prevSolar.getLunar().getMonthInGanZhi();
          }
        }
      }
    } catch (error) {
      console.error('月柱节气精确判断失败，使用默认值:', error);
    }

    setLunarDateStr(
      `${lunarObj.getYearInGanZhiExact()}(${lunarObj.getYearShengXiaoExact()})年 ${lunarObj.getMonthInChinese()}月${lunarObj.getDayInChinese()} ${lunarHourTrue
        .getSixtyCycle()
        .getName()}时`,
    );

    setFourPillars({ year: sixtyCycleYear, month: sixtyCycleMonth, day: sixtyCycleDay, hour: sixtyCycleHour });

    const daYunList = calculateDaYunWithAge(
      standardYear,
      standardMonth,
      standardDay,
      standardHour,
      standardMinute,
      sixtyCycleYear,
      sixtyCycleMonth,
      gender,
      8,
    );
    setDaYun(daYunList);

    let validHour = Math.floor(trueSolarHour);
    if (validHour < 0) validHour = 0;
    if (validHour > 23) validHour = 23;

    const birthdayStr = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    const shiChenIndex = hourToShiChenIndex(validHour);

    setZiWeiBirthday(birthdayStr);
    setZiWeiBirthTime(shiChenIndex);
    setZiWeiGender(gender === 'male' ? '男' : '女');
  };

  const onFinish = async (values: FormValues) => {
    const { birthDateTime, longitude, gender, distinguishZiShi } = values;
    if (!birthDateTime) {
      message.error('请选择出生日期和时间');
      return;
    }

    const { date } = birthDateTime;
    const longitudeNum = Number(longitude);
    if (Number.isNaN(longitudeNum)) {
      message.error('请输入有效的经度，例如 116.4');
      return;
    }

    const standard = date.second(0).millisecond(0);
    performPaiPan({
      year: standard.year(),
      month: standard.month() + 1,
      day: standard.date(),
      hour: standard.hour(),
      minute: standard.minute(),
      longitude: longitudeNum,
      gender,
      distinguishZiShi: distinguishZiShi || false,
    });

    setCareerAnswer('');
    setCareerPreviewLocked(false);
    setCareerUnlocked(false);
    setCareerReportToken('');
    setPaidCareerOrder('');
    setLockedSectionTitles([]);
    setVedicDebug('');
    setFollowUpQuestion('');
    setFollowUpHistory([]);
  };

  const generateCareerReport = async () => {
    if (!fourPillars || daYun.length === 0) {
      message.error('请先完成排盘');
      return;
    }

    try {
      await form.validateFields(['currentJob', 'careerConfusion']);
    } catch {
      message.error('请先填写当前工作背景和事业困惑');
      return;
    }

    const values = form.getFieldsValue();
    const { birthDateTime, city, gender, longitude, latitude, currentJob, careerConfusion, rationalVersion } = values;

    const birthInfo = {
      solar: birthDateTime.date.format('YYYY-MM-DD HH:mm'),
      city,
      longitude,
      latitude,
      gender,
      currentJob: currentJob || '',
      careerConfusion: careerConfusion || '',
    };

    setCareerAnswer('');
    setCareerPreviewLocked(siteConfig.isPaidMode);
    setCareerUnlocked(false);
    setCareerReportToken('');
    setFollowUpHistory([]);
    setAiLoading(true);
    try {
      const resp = await runReportStream('/api/ziwei-career', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fourPillars,
          daYun,
          gender,
          birthInfo,
          ziWeiData,
          currentJob: currentJob || '',
          careerConfusion: careerConfusion || '',
          rationalVersion: Boolean(rationalVersion),
        }),
      }, (text) => { setCareerAnswer(text); setAiLoading(false); });

      if (!resp.ok) {
        let errorMessage = '事业分析报告生成失败';
        try {
          const errorData = await resp.json();
          errorMessage = errorData.error || errorData.details || errorMessage;
        } catch {
          // ignore json parse error
        }
        throw new Error(errorMessage);
      }

      const data = await resp.json();
      const showPaidPreview = data.access ? data.access === 'preview' : siteConfig.isPaidMode && Boolean(data.reportToken);
      setCareerAnswer((showPaidPreview ? data.previewAnswer : data.answer) || '暂无报告内容');
      setCareerPreviewLocked(showPaidPreview);
      setCareerUnlocked(!showPaidPreview);
      setCareerReportToken(data.reportToken || '');
      setLockedSectionTitles(Array.isArray(data.lockedSectionTitles) ? data.lockedSectionTitles : []);
      console.log('[career-paywall] client state', {
        siteMode: siteConfig.mode,
        isPaidMode: siteConfig.isPaidMode,
        isPaidPreview: showPaidPreview,
        hasReportToken: Boolean(data.reportToken),
        lockedSectionCount: Array.isArray(data.lockedSectionTitles) ? data.lockedSectionTitles.length : 0,
      });
      setVedicDebug(data.vedicDebug || '');
      setFollowUpQuestion('');
      setFollowUpHistory([]);
      console.log('印度占星原始结果', data.vedicDebug || '');
      message.success('个人发展分析已生成');
    } catch (error: any) {
      console.error(error);
      message.error(error.message || '生成失败，请稍后重试');
    } finally {
      setAiLoading(false);
    }
  };

  const onReset = () => {
    form.resetFields();
    localStorage.removeItem(CAREER_FORM_DRAFT_STORAGE_KEY);
    setFourPillars(null);
    setDaYun([]);
    setLunarDateStr('');
    setTrueSolarTimeStr('');
    setShiChenBoundaryHint('');
    setCareerAnswer('');
    setCareerPreviewLocked(false);
    setCareerUnlocked(false);
    setCareerReportToken('');
    setPaidCareerOrder('');
    setLockedSectionTitles([]);
    setVedicDebug('');
    setFollowUpQuestion('');
    setFollowUpHistory([]);
  };

  const submitFollowUp = async () => {
    if (reportStreaming || followUpStreaming) return;
    if (isCareerPaywallActive) {
      message.info('解锁完整内容后，可获得本次分析的两次补充提问机会');
      return;
    }

    if (!careerAnswer.trim()) {
      message.error('请先生成个人发展分析');
      return;
    }

    if (remainingFollowUps <= 0) {
      message.error(`本次分析的 ${CAREER_FOLLOW_UP_LIMIT} 次补充提问机会已用完，请重新生成分析`);
      return;
    }

    if (!followUpQuestion.trim()) {
      message.error('请输入想继续追问的问题');
      return;
    }

    if (!fourPillars || daYun.length === 0) {
      message.error('命盘信息缺失，请重新排盘');
      return;
    }

    const values = form.getFieldsValue();
    const { birthDateTime, city, gender, longitude, latitude, currentJob, careerConfusion, rationalVersion } = values;

    const birthInfo = {
      solar: birthDateTime.date.format('YYYY-MM-DD HH:mm'),
      city,
      longitude,
      latitude,
      gender,
      currentJob: currentJob || '',
      careerConfusion: careerConfusion || '',
    };

    setLiveFollowUp('');
    setFollowUpLoading(true);
    try {
      const resp = await runFollowUpStream('/api/ziwei-career', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fourPillars,
          daYun,
          gender,
          birthInfo,
          ziWeiData,
          currentJob: currentJob || '',
          careerConfusion: careerConfusion || '',
          rationalVersion: Boolean(rationalVersion),
          followUpQuestion: followUpQuestion.trim(),
          previousAnswer: careerAnswer,
          followUpHistory,
          paidOrderId: paidCareerOrder,
        }),
      }, (text) => { setLiveFollowUp(text); });

      if (!resp.ok) {
        let errorMessage = '追问回答生成失败';
        try {
          const errorData = await resp.json();
          errorMessage = errorData.error || errorData.details || errorMessage;
        } catch {
          // ignore
        }
        throw new Error(errorMessage);
      }

      const data = await resp.json();
      const answer = data.answer || '暂无追问回答';
      setFollowUpHistory((prev) => [
        ...prev,
        {
          question: followUpQuestion.trim(),
          answer,
        },
      ]);
      setLiveFollowUp('');
      setFollowUpQuestion('');
      message.success('追问回答已生成');
    } catch (error: any) {
      console.error(error);
      message.error(error.message || '追问失败，请稍后重试');
    } finally {
      setFollowUpLoading(false);
    }
  };

  useEffect(() => {
    const routeState = location.state as { prefillCareerData?: CareerPrefillPayload } | null;
    const routePrefill = routeState?.prefillCareerData;
    const savedDraft = loadCareerFormDraft();
    const prefillData: CareerFormDraft | null = routePrefill
      ? {
        birthDateTime: routePrefill.birthDateTime.date,
        isLunar: routePrefill.birthDateTime.isLunar,
        gender: routePrefill.gender,
        city: routePrefill.city,
        longitude: routePrefill.longitude,
        latitude: routePrefill.latitude,
        distinguishZiShi: routePrefill.distinguishZiShi,
        careerConfusion: routePrefill.careerConfusion,
        rationalVersion: routePrefill.rationalVersion,
      }
      : savedDraft;

    if (!prefillData) {
      form.setFieldsValue({
        birthDateTime: { date: dayjs(), isLunar: false },
        gender: '',
        distinguishZiShi: false,
        rationalVersion: false,
      });
      return;
    }

    const parsedDate = prefillData.birthDateTime ? dayjs(prefillData.birthDateTime) : null;
    const birthDateTime = {
      date: parsedDate?.isValid() ? parsedDate : dayjs(),
      isLunar: Boolean(prefillData.isLunar),
    };

    form.setFieldsValue({
      birthDateTime,
      gender: prefillData.gender,
      city: prefillData.city,
      longitude: prefillData.longitude,
      latitude: prefillData.latitude,
      distinguishZiShi: prefillData.distinguishZiShi || false,
      currentJob: prefillData.currentJob,
      careerConfusion: prefillData.careerConfusion,
      rationalVersion: Boolean(prefillData.rationalVersion),
    });

    if ((prefillData.gender === 'male' || prefillData.gender === 'female') && Number.isFinite(Number(prefillData.longitude))) {
      performPaiPan({
        solar: birthDateTime.date.format('YYYY-MM-DD HH:mm'),
        longitude: Number(prefillData.longitude),
        gender: prefillData.gender,
        distinguishZiShi: prefillData.distinguishZiShi || false,
      });
    }

    setCareerAnswer('');
    setCareerPreviewLocked(false);
    setCareerUnlocked(false);
    setCareerReportToken('');
    setPaidCareerOrder('');
    setLockedSectionTitles([]);
  }, [form, location.state]);

  const unlockCareerReport = async (tradeNo: string) => {
    const response = await fetch(`/api/xunhupay-report?tradeNo=${encodeURIComponent(tradeNo)}`);
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || '支付状态尚未确认，请稍后重试');
    const report = decodeCareerReport(data.data || '');
    if (!report?.aiAnswer) throw new Error('完整内容读取失败，请联系客服并提供订单号');
    setCareerAnswer(report.aiAnswer);
    setCareerUnlocked(true);
    setCareerPreviewLocked(false);
    setCareerReportToken('');
    setPaidCareerOrder(tradeNo);
    message.success('完整内容已解锁，已附赠两次补充提问机会');
  };

  useEffect(() => {
    const tradeNo = new URLSearchParams(location.search).get('order');
    if (!tradeNo || !siteConfig.isPaidMode) return;
    unlockCareerReport(tradeNo).catch(() => {
      // 支付平台跳回页面时，异步通知可能仍在路上；按钮轮询会继续处理确认。
    });
  }, [location.search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (reportRef.current) {
        reportRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [reportHasContent]);

  return (
    <ConfigProvider locale={zhCN}>
      <WeChatGuide visible={isWeChat} />
      <AILoadingAnimation visible={aiLoading} />
      <AILoadingAnimation
        visible={followUpLoading && !liveFollowUp}
        title="正在生成追问回答..."
        subtitle="结合主报告结论与当前追问继续推演"
      />
      <div className="app-container">
        <div className="app-layout">
          <div className="center-content">
            <div style={{ width: '100%', maxWidth: '900px' }}>
              <div className="form-card">
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                  <Button onClick={() => navigate(homePath)}>
                    返回首页
                  </Button>
                </div>
                <h1 className="form-title">个人发展分析</h1>
                <h2 className="hero-subtitle">基于紫微斗数，定位你的财富跃迁路径</h2>
                <Form
                  form={form}
                  name="career-form"
                  onFinish={onFinish}
                  layout="vertical"
                >
                  <Form.Item
                    name="birthDateTime"
                    label="出生时间"
                    rules={[{ required: true, message: '请选择出生时间' }]}
                  >
                    <BirthDateTimePicker />
                  </Form.Item>

                  <Form.Item
                    name="gender"
                    label="性别"
                    rules={[{ required: true, message: '请选择性别' }]}
                  >
                    <Select
                      placeholder="请选择性别"
                      options={[
                        { value: 'male', label: '男' },
                        { value: 'female', label: '女' },
                      ]}
                    />
                  </Form.Item>

                  <Form.Item
                    name="city"
                    label="出生城市"
                    rules={[{ required: true, message: '请选择出生城市' }]}
                  >
                    <Select
                      showSearch
                      placeholder="请选择城市"
                      options={cityOptions}
                      onChange={handleCityChange}
                      optionFilterProp="searchText"
                      filterOption={cityFilterOption}
                    />
                  </Form.Item>

                  {/* 隐藏字段：经度值由城市选择自动填充 */}
                  <Form.Item name="longitude" hidden>
                    <input type="number" />
                  </Form.Item>

                  <Form.Item
                    name="currentJob"
                    label="当前工作背景"
                    tooltip="描述你目前的职业状态，例如：互联网产品经理工作3年、体制内公务员、创业者等"
                    rules={[{ required: true, message: '请填写当前工作背景' }]}
                  >
                    <input
                      type="text"
                      placeholder="例如：互联网产品经理，工作3年，想转型做自由职业"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        fontSize: '14px',
                        outline: 'none',
                        transition: 'border-color 0.3s'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#d4af6a'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#d9d9d9'}
                    />
                  </Form.Item>

                  <Form.Item
                    name="careerConfusion"
                    label="事业困惑"
                    tooltip="描述你目前在事业发展中遇到的困惑或想咨询的问题"
                    rules={[{ required: true, message: '请填写事业困惑' }]}
                  >
                    <textarea
                      placeholder="例如：现在的工作看不到上升空间，不知道是该跳槽还是继续熬；或者想创业但不确定时机是否成熟..."
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        fontSize: '14px',
                        outline: 'none',
                        resize: 'vertical',
                        minHeight: '80px',
                        transition: 'border-color 0.3s'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = '#d4af6a'}
                      onBlur={(e) => e.currentTarget.style.borderColor = '#d9d9d9'}
                    />
                  </Form.Item>

                  <Form.Item
                    name="rationalVersion"
                    valuePropName="checked"
                    style={{ marginBottom: '18px' }}
                  >
                    <div
                      style={{
                        padding: '14px 16px',
                        borderRadius: '14px',
                        border: '1px solid rgba(212, 175, 106, 0.45)',
                        background: 'linear-gradient(135deg, rgba(212, 175, 106, 0.16) 0%, rgba(255, 249, 237, 0.96) 100%)',
                        boxShadow: '0 8px 24px rgba(212, 175, 106, 0.12)',
                      }}
                    >
                      <Checkbox>
                        <span style={{ fontSize: '16px', color: '#6f5222', marginRight: '8px', fontWeight: 700 }}>
                          使用理性版报告
                        </span>
                      </Checkbox>
                      <div style={{ marginTop: '10px', paddingLeft: '28px', fontSize: '13px', lineHeight: '1.8', color: '#7a6540' }}>
                        勾选后更偏结构化、理性推演、少一点情绪感。
                        <br />
                      </div>
                    </div>
                  </Form.Item>

                  <Form.Item
                    name="distinguishZiShi"
                    valuePropName="checked"
                    style={{ marginBottom: '24px' }}
                  >
                    <Checkbox>
                      <span style={{ fontSize: '15px', color: '#333', marginRight: '6px' }}>
                        区分早晚子时
                      </span>
                      <Tooltip
                        title={
                          <div style={{ lineHeight: '1.8', fontSize: '13px' }}>
                            <div style={{ marginBottom: '8px', fontWeight: 600, color: '#ffd666' }}>
                              子时日柱计算规则（真太阳时）：
                            </div>
                            <div style={{ marginBottom: '6px' }}>
                              <strong>🔲 传统算法（默认）：</strong>
                            </div>
                            <div style={{ marginLeft: '16px', marginBottom: '10px', color: '#d9d9d9' }}>
                              23:00-01:00 统一算第二天
                            </div>
                            <div style={{ marginBottom: '6px' }}>
                              <strong>☑️ 现代算法（勾选后）：</strong>
                            </div>
                            <div style={{ marginLeft: '16px', color: '#d9d9d9' }}>
                              • 23:00-00:00 算当天（早子时）<br />
                              • 00:00-01:00 算第二天（晚子时）
                            </div>
                          </div>
                        }
                        overlayStyle={{ maxWidth: '320px' }}
                      >
                        <QuestionCircleOutlined style={{ color: '#d4af6a', cursor: 'help' }} />
                      </Tooltip>
                    </Checkbox>
                  </Form.Item>

                  <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                    <Button type="primary" htmlType="submit" className="submit-button">
                      开始排盘
                    </Button>
                    <Button htmlType="button" onClick={onReset} className="reset-button">
                      重置
                    </Button>
                  </div>
                </Form>
              </div>

              {fourPillars && (
                <div className="result-card" style={{ marginTop: '24px' }}>
                  <h2 className="result-title">排盘结果</h2>
                  <div style={{ textAlign: 'center', marginBottom: 20, color: '#666' }}>
                    <p>公历（北京时间）：{form.getFieldValue('birthDateTime')?.date?.format('YYYY年MM月DD日 HH:mm')}</p>
                    <p style={{ color: '#d4af6a', fontWeight: 500, fontSize: '14px', margin: '8px 0' }}>真太阳时：{trueSolarTimeStr}</p>
                    {shiChenBoundaryHint && (
                      <div style={{
                        margin: '10px auto 0',
                        maxWidth: '720px',
                        padding: '10px 14px',
                        borderRadius: '10px',
                        background: 'rgba(212, 175, 106, 0.12)',
                        border: '1px solid rgba(212, 175, 106, 0.28)',
                        color: '#8a6a3f',
                        fontSize: '13px',
                        lineHeight: 1.7,
                      }}>
                        {shiChenBoundaryHint}
                      </div>
                    )}
                    <p>农历：{lunarDateStr}</p>
                  </div>
                  <FourPillars {...fourPillars} />

                  <div style={{ marginTop: '24px', textAlign: 'center' }}>
                    <Button type="primary" size="large" className="submit-button" disabled={reportStreaming} onClick={generateCareerReport}>
                      生成我的个人发展分析
                    </Button>
                  </div>
                </div>
              )}

              <StreamingStatus active={reportStreaming && Boolean(careerAnswer)} error={streamError} />
              {careerAnswer && (
                <div ref={reportRef} className="result-card" style={{ marginTop: '24px', marginBottom: '24px' }}>
                  <div className="career-report-content">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                      <h2 className="result-title" style={{ margin: 0 }}>个人发展分析报告</h2>
                      <Button
                        type="primary"
                        disabled={reportStreaming || Boolean(streamError) || followUpStreaming} onClick={() => exportImage('.career-report-content')}
                        style={{
                          background: 'linear-gradient(135deg, #d4af6a 0%, #b8935d 100%)',
                          border: 'none',
                          fontWeight: 600
                        }}
                      >
                        🖼️ 保存为图片
                      </Button>
                    </div>
                    
                    {/* 保存提示 */}
                    <div style={{
                      margin: '0 0 20px',
                      padding: '14px 18px',
                      background: 'linear-gradient(135deg, rgba(212, 175, 106, 0.15) 0%, rgba(212, 175, 106, 0.08) 100%)',
                      borderRadius: '10px',
                      border: '1px solid rgba(212, 175, 106, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div style={{ fontSize: '24px' }}>💡</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '15px', fontWeight: 600, color: '#d4af6a', marginBottom: '4px' }}>
                          报告内容较长，建议先保存再慢慢品读
                        </div>
                        <div style={{ fontSize: '13px', color: '#666' }}>
                          点击"保存为图片"按钮，将报告保存到相册，方便随时查看
                        </div>
                      </div>
                    </div>

                    <div className="markdown-content" style={{ color: '#2f2f2f' }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{careerAnswer}</ReactMarkdown>
                    </div>
                    

                    {isCareerPaywallActive && (
                      <div
                        style={{
                          marginTop: '28px',
                          padding: '28px',
                          borderRadius: '18px',
                          border: '1px solid rgba(165, 116, 44, 0.32)',
                          background: 'linear-gradient(135deg, #fff8e8 0%, #fffdf8 45%, #f8f3ff 100%)',
                          boxShadow: '0 18px 44px rgba(116, 84, 36, 0.12)',
                          textAlign: 'center',
                          position: 'relative',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '4px',
                            background: 'linear-gradient(90deg, #c8943f 0%, #7c65d8 52%, #d4af6a 100%)',
                          }}
                        />
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '6px 12px',
                          borderRadius: '999px',
                          background: 'rgba(111, 82, 34, 0.08)',
                          color: '#7b5824',
                          fontSize: '13px',
                          fontWeight: 700,
                          marginBottom: '14px',
                        }}>
                          <span>已解读前两章</span>
                          <span style={{ color: '#a98a52' }}>·</span>
                          <span>剩余决策部分待解锁</span>
                        </div>
                        <div style={{ fontSize: '24px', fontWeight: 800, color: '#3f2c18', lineHeight: 1.35 }}>
                          后半份报告，才是真正帮你做选择的部分
                        </div>
                        <div style={{ margin: '12px auto 20px', maxWidth: '720px', fontSize: '15px', color: '#6b6258', lineHeight: 1.85 }}>
                          前面已经告诉你“你像什么类型的人”。解锁后会继续拆到：钱从哪里来、哪条副业能试、哪几年该冲或该守，以及最容易把钱漏掉的地方。
                        </div>
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                            gap: '12px',
                            margin: '0 auto 20px',
                            maxWidth: '760px',
                            textAlign: 'left',
                          }}
                        >
                          {[
                            { title: '搞钱路径', text: '主业怎么换钱，副业从哪切入，哪些钱看着热闹但不适合你。' },
                            { title: '关键年份', text: '未来3-5年哪些窗口适合谈薪、跳槽、接项目，哪些年先别硬冲。' },
                            { title: '漏财防守', text: '投资、合伙、合同、现金流和分账，提前指出最该防的坑。' },
                          ].map((item) => (
                            <div
                              key={item.title}
                              style={{
                                padding: '14px 14px 13px',
                                borderRadius: '12px',
                                background: 'rgba(255, 255, 255, 0.72)',
                                border: '1px solid rgba(212, 175, 106, 0.22)',
                              }}
                            >
                              <div style={{ fontSize: '15px', fontWeight: 800, color: '#5b3d13', marginBottom: '6px' }}>
                                🔒 {item.title}
                              </div>
                              <div style={{ fontSize: '13px', color: '#6f665d', lineHeight: 1.65 }}>
                                {item.text}
                              </div>
                            </div>
                          ))}
                        </div>
                        {lockedSectionTitles.length > 0 && (
                          <div style={{ margin: '0 auto 18px', maxWidth: '760px', fontSize: '13px', color: '#8a6a34', lineHeight: 1.8 }}>
                            本次已生成：{lockedSectionTitles.join('、')}。
                          </div>
                        )}
                        {reportStreaming && <p role="status">正在生成完整报告，现在可用邀请码解锁，解锁后正文会继续实时展示。</p>}
                        <InviteCodeUnlock key={careerReportToken || streamReportToken} product="career" reportToken={careerReportToken || streamReportToken}
                          onUnlocked={(fullAnswer) => {
                            if (fullAnswer) setCareerAnswer(fullAnswer);
                            setCareerUnlocked(true);
                            setCareerPreviewLocked(false);
                            setCareerReportToken('');
                          }} />
                        {careerReportToken && <XunhupayButton
                          product="career"
                          reportToken={careerReportToken}
                          buttonText={`¥${CAREER_PAYMENT_PRICE.toFixed(2)} 解锁后半份决策报告`}
                          onOrderCreated={() => saveCareerFormDraft(form.getFieldsValue() as Partial<FormValues>)}
                          onSuccess={(tradeNo) => {
                            unlockCareerReport(tradeNo).catch((error) => message.error(error.message || '解锁失败，请稍后重试'));
                          }}
                        />}
                        <div style={{ marginTop: '12px', color: '#6f6256', fontSize: '13px', fontWeight: 600 }}>
                          解锁后立即显示完整报告，并附带 2 次针对本命盘的补充追问。
                        </div>
                        <div style={{ marginTop: '8px', color: '#9a9185', fontSize: '12px', lineHeight: 1.6 }}>内容仅供个人兴趣与自我探索参考，不构成职业、投资、医疗、法律或其他专业建议。</div>
                      </div>
                    )}

                    {!isCareerPaywallActive &&                     <div
                      style={{
                        marginTop: '28px',
                        padding: '20px',
                        borderRadius: '14px',
                        border: '1px solid rgba(212, 175, 106, 0.26)',
                        background: 'linear-gradient(135deg, rgba(250, 247, 240, 0.95) 0%, rgba(255, 252, 246, 0.95) 100%)',
                      }}
                    >
                      <div style={{ fontSize: '17px', fontWeight: 600, color: '#6f5222', marginBottom: '10px' }}>
                        继续追问
                      </div>
                      <div style={{ fontSize: '13px', color: '#666', lineHeight: 1.7, marginBottom: '8px' }}>
                        可以继续追问这份命盘里的细节，比如“我今年更适合跳槽还是内部转岗”“副业更适合从咨询还是内容切入”“印占里 D10 更像什么职场环境”。
                      </div>
                      <div style={{ fontSize: '13px', color: remainingFollowUps > 0 ? '#8c6b2f' : '#b42318', lineHeight: 1.7, marginBottom: '12px', fontWeight: 600 }}>
                        本次报告剩余追问次数：{remainingFollowUps} / {CAREER_FOLLOW_UP_LIMIT}
                      </div>
                      <textarea
                        value={followUpQuestion}
                        onChange={(e) => setFollowUpQuestion(e.target.value)}
                        placeholder="例如：如果我接下来半年想换工作，更适合外部跳槽还是先在原公司拿项目？"
                        rows={3}
                        style={{
                          width: '100%',
                          padding: '12px 14px',
                          border: '1px solid #d9d9d9',
                          borderRadius: '10px',
                          fontSize: '14px',
                          outline: 'none',
                          resize: 'vertical',
                          minHeight: '88px',
                          background: '#fff',
                        }}
                      />
                      <div style={{ marginTop: '12px', textAlign: 'right' }}>
                        <Button
                          type="primary"
                          onClick={submitFollowUp}
                          loading={followUpLoading}
                          disabled={remainingFollowUps <= 0 || reportStreaming}
                          style={{
                            background: 'linear-gradient(135deg, #d4af6a 0%, #b8935d 100%)',
                            border: 'none',
                            fontWeight: 600,
                          }}
                        >
                          {remainingFollowUps > 0 ? (followUpLoading ? '追问中...' : '提交追问') : '追问次数已用完'}
                        </Button>
                      </div>
                    </div>

                    }

                {liveFollowUp && <section className="markdown-content" aria-busy={followUpStreaming}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{liveFollowUp}</ReactMarkdown>
                  <StreamingStatus active={followUpStreaming} error={followUpStreamError} />
                </section>}
                    {!isCareerPaywallActive && followUpHistory.length > 0 && (
                      <div style={{ marginTop: '24px' }}>
                        <div style={{ fontSize: '17px', fontWeight: 600, color: '#6f5222', marginBottom: '14px' }}>
                          追问记录
                        </div>
                        {[...followUpHistory].reverse().map((item, index) => (
                          <div
                            key={`${followUpHistory.length - index}-${item.question}`}
                            style={{
                              marginBottom: '16px',
                              padding: '18px',
                              borderRadius: '14px',
                              background: '#fffdf9',
                              border: '1px solid rgba(212, 175, 106, 0.2)',
                            }}
                          >
                            <div style={{ fontSize: '14px', fontWeight: 600, color: '#8c6b2f', marginBottom: '8px' }}>
                              追问 {followUpHistory.length - index}
                            </div>
                            <div style={{ fontSize: '14px', color: '#333', lineHeight: 1.7, marginBottom: '12px' }}>
                              <strong>你问：</strong>{item.question}
                            </div>
                            <div className="markdown-content" style={{ color: '#2f2f2f' }}>
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>{item.answer}</ReactMarkdown>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* 底部保存按钮 */}
                    <div style={{
                      marginTop: '40px',
                      padding: '32px 24px',
                      background: 'linear-gradient(135deg, rgba(212, 175, 106, 0.08) 0%, rgba(212, 175, 106, 0.03) 100%)',
                      borderRadius: '16px',
                      border: '2px solid rgba(212, 175, 106, 0.2)',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '32px', marginBottom: '12px' }}>✨</div>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: 600,
                        color: '#333',
                        marginBottom: '8px'
                      }}>
                        感谢您阅读完整内容
                      </h3>
                      <p style={{
                        fontSize: '14px',
                        color: '#666',
                        lineHeight: '1.7',
                        marginBottom: '20px'
                      }}>
                        点击"保存为图片"，将这份个人发展分析保存到相册，<br />
                        随时回顾，把握事业发展机遇。
                      </p>
                      <Button
                        type="primary"
                        size="large"
                        disabled={reportStreaming || Boolean(streamError) || followUpStreaming} onClick={() => exportImage('.career-report-content')}
                        style={{
                          background: 'linear-gradient(135deg, #d4af6a 0%, #b8935d 100%)',
                          border: 'none',
                          height: '48px',
                          padding: '0 40px',
                          fontSize: '16px',
                          fontWeight: 600,
                          boxShadow: '0 6px 20px rgba(212, 175, 106, 0.3)'
                        }}
                      >
                        📸 立即保存长图
                      </Button>
                    </div>

                    <div style={{ marginTop: '32px' }}>
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <img src={donateQrUrl} width={400} height={400} alt="赞赏码" />
                      </div>
                      <div style={{
                        marginTop: '14px',
                        padding: '14px 16px',
                        background: 'linear-gradient(135deg, rgba(212, 175, 106, 0.1) 0%, rgba(248, 246, 255, 0.75) 100%)',
                        borderRadius: '10px',
                        border: '1px solid rgba(212, 175, 106, 0.35)',
                        color: '#6b5a3a',
                        fontSize: '14px',
                        lineHeight: '1.9',
                        textAlign: 'center'
                      }}>
                        <div>这个站没有团队、没有融资、没有广告位，只有我一个人硬扛。</div>
                        <div>每次生成报告都在烧 GPU 算力和接口费用，很多时候你看到一行字，我背后是实打实地在倒贴成本。</div>
                        <div>如果这份报告对你有一点点帮助，扫码支持一下，哪怕是一杯矿泉水的钱，都能让我多撑一天。</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
};

export default CareerPage;
